import { memo, useCallback } from 'react'

import { TextField } from '@mui/material'
import { OrganizationInsert } from '@nativewaves/nw-cloud-sdk/org'
import { Controller, useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { useShallow } from 'zustand/shallow'

import {
  useSubmissionProgressStore,
  selectSubmissionProgressSetters,
  SubmissionProgress,
} from '@shared/components/SubmissionProgress'
import { DialogFormContent } from '@shared/layouts'
import { useOrganizationCreateMutation } from 'hooks/mutations/org'

type FormStructure = OrganizationInsert

const NewDialogRaw = () => {
  const { t } = useTranslation(['common', 'domain'])

  const { setCurrentStep } = useSubmissionProgressStore(useShallow(selectSubmissionProgressSetters))

  const { mutate: createOrganization, isSuccess, isError } = useOrganizationCreateMutation()

  const { handleSubmit, control } = useForm<FormStructure>({})

  const handleFormSubmit = useCallback(
    (data: FormStructure) => {
      setCurrentStep({ value: 10, description: `${t('domain:My.Organization.creating')} ${data.name}` })

      createOrganization(
        { organizationInsert: data },
        {
          onSuccess: (data) => {
            setCurrentStep({ value: 100, description: `${data.name} ${t('domain:My.Organization.created')}` })
          },
          onError: (_, variables) => {
            setCurrentStep({
              value: 100,
              description: `${variables.organizationInsert.name} ${t('domain:My.Organization.couldNotBeCreated')}`,
            })
          },
        },
      )
    },
    [t, createOrganization, setCurrentStep],
  )

  return (
    <DialogFormContent
      onSubmit={handleSubmit(handleFormSubmit)}
      title={t('domain:My.Organization.addOrganization')}
      description={t('domain:My.Organization.entityCreateDescription')}
      SubmitProgressZone={<SubmissionProgress isSuccess={isSuccess} isError={isError} />}
    >
      <Controller
        name="name"
        control={control}
        render={({ field }) => (
          <TextField
            {...field}
            autoFocus
            margin="dense"
            label={t('name')}
            type="text"
            fullWidth
            variant="standard"
            required
            autoComplete="off"
          />
        )}
      />
    </DialogFormContent>
  )
}

export const NewDialog = memo(NewDialogRaw)
